/**
 * Pull a human-readable message from Laravel / portal API error bodies.
 *
 * Handles:
 * - `{ data: null, errors: [{ code, message, fields }] }` (validation envelope)
 * - `{ message: "..." }` (flat 422, e.g. submit incompleteness)
 * - Angular HttpErrorResponse wrapping either of the above
 */
export function parseApiError(
  err: unknown,
  fallback = 'Something went wrong. Please try again.'
): string {
  if (err == null) return fallback;
  if (typeof err === 'string' && err.trim()) return err.trim();
  if (err instanceof Error && err.message && !(err as { error?: unknown }).error) {
    // Prefer HttpErrorResponse body when present; plain Error.message otherwise.
  }

  const body = extractBody(err);
  if (!body) {
    if (err instanceof Error && err.message) return err.message;
    return fallback;
  }

  const parts: string[] = [];

  const envelopeErrors = Array.isArray(body['errors']) ? body['errors'] : null;
  if (envelopeErrors?.length) {
    const first = envelopeErrors[0];
    if (first && typeof first === 'object') {
      const entry = first as Record<string, unknown>;
      if (typeof entry['message'] === 'string' && entry['message'].trim()) {
        parts.push(entry['message'].trim());
      }
      const fields = entry['fields'];
      if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
        for (const msgs of Object.values(fields as Record<string, unknown>)) {
          if (Array.isArray(msgs)) {
            for (const m of msgs) {
              if (typeof m === 'string' && m.trim()) parts.push(m.trim());
            }
          } else if (typeof msgs === 'string' && msgs.trim()) {
            parts.push(msgs.trim());
          }
        }
      }
      const missing = entry['missing_documents'];
      if (Array.isArray(missing)) {
        for (const doc of missing) {
          if (doc && typeof doc === 'object') {
            const label = (doc as { label?: unknown }).label;
            if (typeof label === 'string' && label.trim()) {
              parts.push(`Missing document: ${label.trim()}`);
            }
          }
        }
      }
    }
  }

  // Legacy Laravel shape: errors: { field: ["msg"] }
  if (!parts.length && body['errors'] && typeof body['errors'] === 'object' && !Array.isArray(body['errors'])) {
    for (const msgs of Object.values(body['errors'] as Record<string, unknown>)) {
      if (Array.isArray(msgs) && typeof msgs[0] === 'string') {
        parts.push(msgs[0]);
        break;
      }
    }
  }

  if (typeof body['message'] === 'string' && body['message'].trim()) {
    const msg = body['message'].trim();
    if (!parts.includes(msg)) parts.unshift(msg);
  }

  // Paystack / payment endpoints: `{ error: "Invalid Email Address Passed" }`
  if (typeof body['error'] === 'string' && body['error'].trim()) {
    const msg = body['error'].trim();
    if (!parts.includes(msg)) parts.unshift(msg);
  }

  if (parts.length) {
    // Dedupe while preserving order
    return [...new Set(parts)].join(' ');
  }

  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function extractBody(err: unknown): Record<string, unknown> | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as { error?: unknown; message?: unknown };
  if (e.error && typeof e.error === 'object') {
    return e.error as Record<string, unknown>;
  }
  if ('errors' in e || 'message' in e || 'error' in e) {
    return e as Record<string, unknown>;
  }
  return null;
}
